import React, { useEffect, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import "./CertificateLanding.css";
import AnimatedGradientText from "../TechProfile/AnimatedGradient";
import EducationTopBar from "../topbar/EducationTopBar";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import useWindowResize from "../../../utils/WindowResize";
import { Button } from "@mui/material";
import Roadmap from "./Roadmap";
import { redirectToPageAction } from "../../../actions/userActions";
import { useDispatch, useSelector } from "react-redux";
import RecognizedBySection from "./RecognizedBySection";
import TestimonialsCarousel from "./TestimonialsCarousel";
import MarqueeScroll from "../reusables/MarqueeScroll";
import { workshopReviews } from "../../../utils/constants";
import WorkshopFooterPage from "../Workshops/WorkshopFooter";
import McqSelectionPage from "./McqSelectionPage";
import { getCertificateCategoryListAction } from "../../../actions/MyCertificates/certificatesAction";
import { getBadgeFieldsAction } from "../../../actions/Badge/badgeActions";

// Replace with your actual badge image paths
const badges = [
  "https://pub-1faf68ce26a54cbf8203a339879a01d6.r2.dev/badges/css-frontend-badge.png",
  "https://pub-1faf68ce26a54cbf8203a339879a01d6.r2.dev/badges/tailwind-frontend-badge.png",
  "https://pub-1faf68ce26a54cbf8203a339879a01d6.r2.dev/badges/html-frontend-badge.png",
  "https://pub-1faf68ce26a54cbf8203a339879a01d6.r2.dev/badges/nodejs-backend-badge.png",
];

// Helper component to animate sections on scroll
const AnimateOnScroll = ({
  children,
  className,
  variants,
  threshold = 0.2,
}) => {
  const controls = useAnimation();
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          controls.start("visible");
          observer.disconnect();
        }
      },
      { threshold },
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [controls, threshold]);

  return (
    <motion.section
      ref={ref}
      className={className}
      variants={variants}
      initial="hidden"
      animate={controls}
    >
      {children}
    </motion.section>
  );
};

const CertificateLandingComponent = () => {
  const { userInfo } = useSelector((state) => state.userLogin);
  const { technologyFields } = useSelector(
    (state) => state.technologyFieldsStore,
  );
  const { categories } = useSelector(
    (state) => state.certificateCategoryListStore,
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const exploreCertificationRef = useRef(null);
  const isSmallScreen = useWindowResize();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleOpenDrawer = () => {
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  useEffect(() => {
    dispatch(getBadgeFieldsAction());
    dispatch(getCertificateCategoryListAction());
  }, []);

  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.3 } },
  };

  const itemVariants = {
    hidden: { opacity: 1, scale: 1 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
  };

  // Floating animation for individual badges
  const floatVariants = {
    float: (i) => ({
      y: [0, -10 + i * 2, 0],
      transition: {
        repeat: Infinity,
        duration: 3 + i * 0.5,
        ease: "easeInOut",
      },
    }),
  };

  const scrollToChooseProblem = () => {
    const element = exploreCertificationRef.current;
    if (element) {
      const yOffset = -80; // adjust based on header height
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;

      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const TechJobCard = ({ image, title, id }) => {
    return (
      <motion.div
        className="techjob-card"
        whileHover={{ y: -8, transition: { duration: 0.2 } }}
        variants={itemVariants}
        onClick={() => history.push(`/certificates/list/${id}`)}
      >
        <div className="techjob-card-image-container">
          <img src={image} alt={title} className="techjob-card-image" />
        </div>
        <h3 className="techjob-card-title">{title}</h3>
        <Button
          className="techjob-card-button"
          onClick={() => history.push(`/certificates/list/${id}`)}
          aria-label={`Explore roles for ${title}`}
        >
          Explore Roles
        </Button>
      </motion.div>
    );
  };

  return (
    <div>
      <EducationTopBar
        logo={
          <div
            onClick={() => history.push("/")}
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          >
            <img
              className={isSmallScreen ? "mobile-logo" : "desktop-logo"}
              src="https://res.cloudinary.com/cqn/image/upload/v1740519381/white_acetechies_logo_vsdhp5.png"
              alt=""
            />
          </div>
        }
      />
      <div className="certificatelanding-container">
        <AnimateOnScroll
          className="certificatelanding-hero"
          variants={containerVariants}
        >
          <div className="certificatelanding-hero-content">
            <div className="certificatelanding-hero-text">
              <motion.h1
                className="certificatelanding-title"
                variants={itemVariants}
              >
                <div>
                  Master Tech <AnimatedGradientText text={"Skills"} />
                </div>
                <div>
                  Get <AnimatedGradientText text={"Certified"} />
                </div>
                <div>
                  Land Tech <AnimatedGradientText text={"Job"} />
                </div>
              </motion.h1>

              <motion.p
                className="certificatelanding-hero-subtitle"
                variants={itemVariants}
                style={{ margin: "10px 0px", lineHeight: "22px" }}
              >
                Take a quick MCQ. Earn a free badge in 5 minutes
              </motion.p>

              <motion.div
                className="certificatelanding-buttons"
                variants={itemVariants}
              >
                <Button
                  onClick={handleOpenDrawer}
                  className="certificatelanding-btn-primary"
                >
                  Earn your Free badge
                </Button>
                <Button
                  onClick={scrollToChooseProblem}
                  className="certificatelanding-btn-secondary"
                >
                  Explore certifications
                </Button>
              </motion.div>
            </div>

            <motion.div
              className="certificatelanding-hero-image"
              variants={itemVariants}
            >
              <img
                src="https://res.cloudinary.com/cqn/image/upload/v1769108559/actechies_certificate_vff7mg.png"
                alt="Certificate illustration"
              />
            </motion.div>
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll
          className="certificatelanding-badges-section"
          variants={containerVariants}
        >
          <motion.h2
            className="certificatelanding-badges-title"
            variants={itemVariants}
          >
            Free MCQs to earn badges and showcase your skills
          </motion.h2>
          <div className="certificatelanding-badges-container">
            <motion.div
              className="certificatelanding-pro-badge"
              variants={itemVariants}
            >
              <img
                src="https://pub-1faf68ce26a54cbf8203a339879a01d6.r2.dev/badges/pro-badge.png"
                alt="Pro Badge"
              />
            </motion.div>
            {badges.map((badge, index) => (
              <motion.div
                key={index}
                className={`certificatelanding-badge-card badge-${index + 1}`}
                custom={index}
                variants={floatVariants}
                animate="float"
              >
                <img src={badge} alt={`Skill Badge ${index + 1}`} />
              </motion.div>
            ))}
          </div>
          <Button
            onClick={handleOpenDrawer}
            className="certificatelanding-btn-primary certificatelanding-badge-cta"
          >
            Earn your Free badge
          </Button>
        </AnimateOnScroll>

        <AnimateOnScroll
          className="certificatelanding-section"
          variants={containerVariants}
        >
          <motion.h2
            className="certificatelanding-section-title"
            variants={itemVariants}
            ref={exploreCertificationRef}
          >
            Land your next Tech Job or Internship
          </motion.h2>
          <motion.p
            className="certificatelanding-section-subtitle"
            variants={itemVariants}
          >
            Get Certified and land your next Internship or Job. Our verified
            certificates place students and professionals in the top 5%.
          </motion.p>
          <motion.div
            className="techjob-cards-container"
            variants={itemVariants}
          >
            {" "}
            {categories?.map((category) => {
              return (
                <TechJobCard
                  image={category?.image?.url}
                  title={category?.title}
                  id={category?.slug}
                />
              );
            })}
          </motion.div>
        </AnimateOnScroll>

        <div style={{ margin: "0px 20px" }}>
          <RecognizedBySection />
        </div>

        <AnimateOnScroll
          className="certificatelanding-section"
          variants={containerVariants}
        >
          <motion.h2
            className="certificatelanding-section-title"
            variants={itemVariants}
          >
            How we help you land your next job or internship
          </motion.h2>
          <motion.p
            className="certificatelanding-section-subtitle"
            variants={itemVariants}
          >
            With our curated certifications, you prove your technical knowledge
            to recruiters. We help you place in the top 2% of candidates.
          </motion.p>
          <Button
            onClick={handleOpenDrawer}
            className="certificatelanding-btn-primary"
          >
            Start Now
          </Button>
          <Roadmap />
          <section className="certificatelanding_firstSection">
            <motion.h1
              className="techPortfolioLandingPage_title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Your Skills Deserve a Stunning Portfolio
            </motion.h1>
            <motion.h3
              className="techPortfolioLandingPage_subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              A stunning, recruiter-friendly portfolio built for you. Just edit
              your details and share your public link.
            </motion.h3>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <Button
                sx={{ fontSize: "22px" }}
                className="certificatelanding-btn-primary"
                onClick={() => {
                  history.push(`/techprofile/edit/${userInfo?._id}`);
                  dispatch(
                    redirectToPageAction("EDIT_TECH_PROFILE_PAGE", {
                      url: `/techprofile/edit/${userInfo?._id}`,
                    }),
                  );
                }}
              >
                View My Portfolio
              </Button>
            </motion.div>
            <motion.video
              className="techPortfolioLandingPage_video"
              autoPlay
              muted
              loop
              playsInline
              src="https://res.cloudinary.com/cqn/video/upload/v1760267401/landing_page_video_fl3b0b.mp4"
              type="video/mp4"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1 }}
            />
          </section>
        </AnimateOnScroll>

        <AnimateOnScroll variants={containerVariants}>
          <TestimonialsCarousel />
        </AnimateOnScroll>

        <AnimateOnScroll variants={containerVariants}>
          <div style={{ marginTop: "0px" }} className="desktop-reviews-section">
            <h1 className="desktop-section-title">Reviews</h1>
            <h3 className="desktop-section-subtitle">
              What our students have to say about us.
            </h3>
            <MarqueeScroll content={workshopReviews} isReview={true} />
          </div>
        </AnimateOnScroll>
        {/* MCQ Selection Drawer */}
        <McqSelectionPage
          open={drawerOpen}
          onClose={handleCloseDrawer}
          techFields={technologyFields}
        />
        <div style={{ marginTop: "50px" }}>
          <WorkshopFooterPage />
        </div>
      </div>
    </div>
  );
};

export default CertificateLandingComponent;
